import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");

  const category = params.get("category") || "All";
  const collection = params.get("collection") || "";
  const sort = params.get("sort") || "popular";
  const pieces = params.get("pieces") || "";

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "All") next.set(key, val); else next.delete(key);
    setParams(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (params.get("search")) qp.set("search", params.get("search"));
    if (category !== "All") qp.set("category", category);
    if (collection) qp.set("collection", collection);
    if (pieces) qp.set("pieces", pieces);
    qp.set("sort", sort);
    qp.set("limit", "100");
    api.get(`/products?${qp.toString()}`).then((r) => {
      setItems(r.data.items); setTotal(r.data.total);
    }).finally(() => setLoading(false));
  }, [params, category, collection, sort, pieces]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(["All", ...r.data.categories]));
    api.get("/collections").then((r) => setCollections(r.data));
  }, []);

  const submitSearch = (e) => { e.preventDefault(); setParam("search", search); };

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
      <div className="mb-10">
        <span className="eyebrow">Explore Collection</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-2" data-testid="shop-title">{collection || (category !== "All" ? category : "All Chocolates")}</h1>
        <p className="text-[#8C7B6E] mt-2">{total} exquisite creation{total !== 1 ? "s" : ""}</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Filters */}
        <aside className="lux-card p-6 h-max lg:sticky lg:top-28 min-w-0" data-testid="shop-filters">
          <form onSubmit={submitSearch} className="flex items-center bg-[#1A0F0B] border border-[#3B251D] rounded-full px-3 py-2 mb-6">
            <SearchIcon size={15} className="text-[#8C7B6E]" />
            <input data-testid="shop-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search"
              className="bg-transparent outline-none text-sm text-[#FDFBF7] ml-2 w-full" />
          </form>

          <h4 className="eyebrow mb-3 flex items-center gap-2"><SlidersHorizontal size={13} /> Category</h4>
          <div className="flex lg:flex-col gap-1 mb-6 overflow-x-auto no-scrollbar -mx-2 px-2 lg:mx-0 lg:px-0">
            {categories.map((c) => (
              <button key={c} data-testid={`filter-cat-${c}`} onClick={() => setParam("category", c)}
                className={`shrink-0 whitespace-nowrap text-left text-sm py-1.5 px-3 rounded-lg transition ${category === c ? "bg-[#D4AF37] text-[#1A0F0B] font-semibold" : "text-[#C8B8AB] hover:bg-[#1A0F0B]"}`}>
                {c}
              </button>
            ))}
          </div>

          <h4 className="eyebrow mb-3">Box Size</h4>
          <div className="flex lg:flex-wrap gap-2 mb-6 overflow-x-auto no-scrollbar -mx-2 px-2 lg:mx-0 lg:px-0" data-testid="filter-pieces">
            {["", "4", "6", "12", "16", "18", "24"].map((n) => (
              <button key={n || "all"} data-testid={`filter-pieces-${n || "all"}`} onClick={() => setParam("pieces", n)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors ${pieces === n ? "bg-[#D4AF37] text-[#1A0F0B] border-[#D4AF37] font-semibold" : "border-[#3B251D] text-[#C8B8AB] hover:border-[#D4AF37]"}`}>
                {n ? `${n} pcs` : "Any"}
              </button>
            ))}
          </div>

          <h4 className="eyebrow mb-3">Collection</h4>
          <div className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar -mx-2 px-2 lg:mx-0 lg:px-0">
            <button onClick={() => setParam("collection", "")} className={`shrink-0 whitespace-nowrap text-left text-sm py-1.5 px-3 rounded-lg ${!collection ? "bg-[#D4AF37] text-[#1A0F0B] font-semibold" : "text-[#C8B8AB] hover:bg-[#1A0F0B]"}`}>All</button>
            {collections.map((c) => (
              <button key={c.name} onClick={() => setParam("collection", c.name)}
                className={`shrink-0 whitespace-nowrap text-left text-sm py-1.5 px-3 rounded-lg ${collection === c.name ? "bg-[#D4AF37] text-[#1A0F0B] font-semibold" : "text-[#C8B8AB] hover:bg-[#1A0F0B]"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </aside>

        {/* Grid */}
        <div className="min-w-0">
          <div className="flex justify-end mb-6">
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger data-testid="shop-sort" className="w-52 bg-[#241611] border-[#3B251D] text-[#FDFBF7]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#241611] border-[#3B251D] text-[#FDFBF7]">
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {Array(6).fill(0).map((_, i) => <div key={i} className="lux-card aspect-[3/4] animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 text-[#8C7B6E]" data-testid="shop-empty">No chocolates match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
              {items.map((p, i) => <ProductCard key={`${p.product_id}-${pieces}`} product={p} index={i} preferTier={pieces} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
